import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { remoteUrl } from '../lib/handoff';
import { useLocale } from '../lib/i18n';

interface Props {
  matchId: string;
  connected: boolean;
  onClose: () => void;
}

/**
 * Hands a phone the remote control for this match.
 *
 * The phone that scans this gets buttons, not a second scoreboard. It still
 * writes to the same event log over the same socket, though, so there is no
 * master and no slave: either device can score, undo or end a frame, and both
 * see the result at once.
 *
 * One code, and one only. This panel used to list every IPv4 address the host
 * owns and redraw the code against whichever was tapped, on the theory that a
 * multi-homed machine needs the operator to say which network the phone is
 * on. In practice a laptop with a VPN adapter has two addresses, exactly one
 * of them works, and the panel was asking a question the server can answer
 * better than a person: `/api/network` already ranks ordinary LAN ranges above
 * carrier-grade NAT and link-local, and `remoteUrl` takes the best one. What
 * the picker actually did was put a second QR code one tap away from the first
 * and leave people wondering which of the two was the real one.
 *
 * The plain link stays. It is the fallback for the cases a camera cannot
 * solve — sending the address by message, or typing it — and unlike a second
 * code it cannot be mistaken for a second offer.
 */
export function RemotePanel({ matchId, connected, onClose }: Props) {
  const { locale, t } = useLocale();
  const [url, setUrl] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const timer = window.setTimeout(() => close.current(), 30_000);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    void remoteUrl(matchId, locale)
      .then(async (link) => {
        if (cancelled) return;
        setUrl(link);
        setImage(await QRCode.toDataURL(link, { width: 512, margin: 1 }));
      })
      // Without this the panel would sit on "Generating…" for ever and the
      // failure would be invisible outside the console.
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t('remote.failed'));
      });
    return () => {
      cancelled = true;
    };
    // The link does not depend on the language beyond the `lang` hint it
    // carries for the phone; `t` is only here for the failure message, and
    // re-drawing a QR code on a language change is cheaper than leaving a
    // stale English error on a translated panel.
  }, [matchId, locale, t]);

  const copy = async (): Promise<void> => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const loopback = url !== null && /^(localhost|127\.|\[?::1)/.test(new URL(url).hostname);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4">
      <div className="panel flex max-h-[calc(100dvh-2rem)] w-full max-w-sm flex-col items-center gap-3 overflow-y-auto">
        <div className="flex w-full items-center">
          <h2 className="text-lg font-bold">{t('remote.title')}</h2>
          <button type="button" onClick={onClose} className="btn-ghost ml-auto px-3 py-1 text-sm">
            {t('remote.close')}
          </button>
        </div>

        <p className="min-h-[7.5rem] shrink-0 text-center text-sm text-white/60 max-sm:min-h-[8.75rem]">{t('remote.description')}</p>

        {image ? (
          <img
            src={image}
            alt={t('remote.alt')}
            className="shrink-0 rounded-xl bg-white p-2"
            width={256}
          />
        ) : error ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <p className="text-center text-sm text-ball-red">
              {t('remote.qrFailed', { error })}
            </p>
            <p className="text-center text-xs text-white/50">{t('remote.useLink')}</p>
          </div>
        ) : (
          <p className="py-16 text-white/50">{t('pair.generating')}</p>
        )}

        {url && (
          <button
            type="button"
            onClick={() => void copy()}
            title={t('pair.copy')}
            className="w-full break-all rounded-xl bg-black/40 px-3 py-2 text-center font-mono text-xs text-white/70"
          >
            {url}
          </button>
        )}
        {copied && <p className="text-xs text-ball-green">{t('pair.copied')}</p>}

        {loopback && (
          <p className="text-center text-xs text-ball-red">{t('remote.loopback')}</p>
        )}

        {!connected && (
          <p className="text-center text-xs text-ball-red">{t('remote.notConnected')}</p>
        )}
      </div>
    </div>
  );
}
